import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const Section = ({ number, title, children }) => (
  <div className="mb-10">
    <h2 className="text-xl font-bold text-slate-900 mb-3 flex items-start gap-3">
      <span className="text-brand-500 font-mono text-base mt-0.5">{number}.</span>
      {title}
    </h2>
    <div className="text-slate-600 leading-relaxed space-y-3 pl-6">{children}</div>
  </div>
);

const TermsPage = () => {
  return (
    <div data-testid="terms-page">
      {/* Hero */}
      <section className="bg-slate-900 text-white py-20 noise-bg">
        <div className="container-custom text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms of Service</h1>
            <p className="text-slate-300">Last updated: March 2026</p>
            <p className="text-slate-400 text-sm mt-2 max-w-xl mx-auto">
              Please read these terms carefully before using VoipHub services. By registering or using our platform you agree to be bound by these terms.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="section-light">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto"
          >
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 md:p-12">

              <Section number="1" title="Acceptance of Terms">
                <p>
                  By accessing or using any service offered by VoipHub ("<strong>the Company</strong>", "<strong>we</strong>", "<strong>us</strong>", or "<strong>our</strong>") at <strong>voiphub.in</strong> or via our portal, API, or SIP infrastructure, you ("<strong>Customer</strong>", "<strong>User</strong>", or "<strong>you</strong>") agree to be legally bound by these Terms of Service and all policies incorporated by reference.
                </p>
                <p>
                  If you do not agree to these terms, you must not access or use our services. Your continued use after any modification to these terms constitutes acceptance of the revised terms.
                </p>
              </Section>

              <Section number="2" title="Description of Services">
                <p>VoipHub provides wholesale and retail Voice over IP (VoIP) termination services, including:</p>
                <ul className="list-disc pl-5 space-y-1 mt-2">
                  <li>International A-Z voice termination to 235+ countries</li>
                  <li>SIP trunk accounts (Silver, Gold, Platinum quality tiers)</li>
                  <li>WebRTC browser-based softphone (on-site dialer)</li>
                  <li>Call center routing with high-CPM tolerance</li>
                  <li>Hosted SIP infrastructure on <strong>sip.voiphub.in</strong></li>
                  <li>Customer portal at <strong>voiphub.in/portal</strong> for account management, call records, and billing</li>
                </ul>
                <p className="mt-3">
                  Services are provided on a prepaid basis. We reserve the right to add, modify, or discontinue any service with reasonable notice.
                </p>
              </Section>

              <Section number="3" title="Account Registration">
                <p>To access our services you must register at <strong>voiphub.in/portal</strong>. By registering you agree to:</p>
                <ul className="list-disc pl-5 space-y-1 mt-2">
                  <li>Provide accurate, current, and complete registration information</li>
                  <li>Maintain the security and confidentiality of your SIP credentials and portal password</li>
                  <li>Accept sole responsibility for all activity under your account</li>
                  <li>Notify us immediately at <a href="mailto:support@voiphub.in" className="text-brand-500 hover:underline">support@voiphub.in</a> or via Telegram <a href="https://t.me/voiphub_support" className="text-brand-500 hover:underline">@voiphub_support</a> of any suspected unauthorized access</li>
                  <li>Not share, sell, or transfer your account credentials to any third party</li>
                </ul>
                <p className="mt-3">
                  We reserve the right to reject any registration or suspend an account at our discretion.
                </p>
              </Section>

              <Section number="4" title="Payment Terms and Free Trial Credits">
                <p>VoipHub operates exclusively on a <strong>prepaid credit system</strong>:</p>
                <ul className="list-disc pl-5 space-y-1 mt-2">
                  <li><strong>Minimum initial deposit:</strong> $100 USD or equivalent</li>
                  <li><strong>Free trial credits</strong> may be granted at our discretion on first account top-up — trial credits are for testing purposes only and are not transferable or refundable</li>
                  <li>All payments are <strong>non-refundable</strong> once credits have been consumed</li>
                  <li>Account credits do not expire while the account remains in good standing (at least one transaction per 12 months)</li>
                  <li>Rates are subject to change; we will provide reasonable notice of significant rate changes</li>
                  <li>Accepted payment methods include: Bitcoin (BTC), USDT, and other methods listed in the portal at time of deposit</li>
                </ul>
                <p className="mt-3">
                  In the event of account suspension due to ToS violation, outstanding prepaid balances are forfeited and non-refundable.
                </p>
              </Section>

              <Section number="5" title="Service Plans and Technical Configuration">
                <p>VoipHub offers three quality tiers, all accessible via the same SIP host:</p>
                <div className="bg-slate-50 rounded-xl p-4 my-3 font-mono text-sm space-y-1">
                  <p><strong>SIP Host:</strong> sip.voiphub.in</p>
                  <p><strong>Silver (Tech Prefix 10003)</strong> — Price-oriented, stable routes</p>
                  <p><strong>Gold (Tech Prefix 10001)</strong> — Full CLI, high ASR/ACD, low PDD</p>
                  <p><strong>Platinum (Tech Prefix 10002)</strong> — Tier 1 direct, highest quality</p>
                </div>
                <p>
                  SIP credentials are issued per account from the customer portal. Each account may have multiple SIP usernames. The customer is responsible for configuring their SIP client, softswitch, or dialer correctly using the provided credentials.
                </p>
              </Section>

              <Section number="6" title="Acceptable Use Policy">
                <p>You agree that VoipHub services will not be used for any of the following, which are strictly prohibited:</p>
                <ul className="list-disc pl-5 space-y-1 mt-2">
                  <li>Any illegal activity under applicable local, national, or international law</li>
                  <li>Fraudulent traffic, including but not limited to International Revenue Share Fraud (IRSF), robocalling, or call pumping</li>
                  <li>Transmission of unsolicited commercial communications (spam calls)</li>
                  <li>Impersonation, caller ID spoofing for fraudulent purposes, or unauthorized use of third-party numbers</li>
                  <li>Distribution of malware, harassment, or any content that violates third-party rights</li>
                  <li>Traffic that causes network congestion, degradation, or harm to our infrastructure or other customers</li>
                  <li>Reselling our services without prior written consent</li>
                  <li>Bypassing any security, fraud detection, or rate-limiting mechanisms</li>
                </ul>
                <p className="mt-3">
                  We actively monitor traffic patterns. Accounts identified as generating prohibited traffic may be suspended immediately without prior notice.
                </p>
              </Section>

              <Section number="7" title="Service Level and Uptime">
                <p>
                  VoipHub targets <strong>99.9% platform uptime</strong>, measured monthly excluding scheduled maintenance windows. Voice call quality is subject to factors outside our direct control including:
                </p>
                <ul className="list-disc pl-5 space-y-1 mt-2">
                  <li>Customer-side internet connectivity and router configuration</li>
                  <li>Destination carrier availability and routing changes</li>
                  <li>Force majeure events (natural disasters, government actions, etc.)</li>
                </ul>
                <p className="mt-3">
                  Service interruptions do not entitle the customer to refunds unless otherwise agreed in writing. We will use reasonable efforts to notify customers of planned maintenance in advance via email or Telegram.
                </p>
              </Section>

              <Section number="8" title="Limitation of Liability">
                <p>
                  To the maximum extent permitted by applicable law, VoipHub shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to: lost revenue, lost profits, loss of business, or loss of data arising from:
                </p>
                <ul className="list-disc pl-5 space-y-1 mt-2">
                  <li>Use or inability to use our services</li>
                  <li>Unauthorized access to your account or communications</li>
                  <li>Any third-party conduct or content</li>
                  <li>Service interruption or degradation</li>
                </ul>
                <p className="mt-3">
                  Our total cumulative liability to you for any claim shall not exceed the total amount you paid to VoipHub in the <strong>30 days immediately preceding the event</strong> giving rise to the claim.
                </p>
              </Section>

              <Section number="9" title="Intellectual Property">
                <p>
                  All content on voiphub.in, including but not limited to software, text, graphics, logos, and the VoipHub brand, is the property of VoipHub and is protected by applicable intellectual property laws. You may not reproduce, distribute, or create derivative works without express written permission.
                </p>
              </Section>

              <Section number="10" title="Privacy">
                <p>
                  Your use of VoipHub services is also governed by our <Link to="/privacy" className="text-brand-500 hover:underline">Privacy Policy</Link>, which is incorporated into these Terms by reference. By using our services you consent to the collection and use of your information as described therein.
                </p>
              </Section>

              <Section number="11" title="Account Suspension and Termination">
                <p>We may suspend or terminate your account immediately and without notice if:</p>
                <ul className="list-disc pl-5 space-y-1 mt-2">
                  <li>You violate any provision of these Terms</li>
                  <li>We reasonably believe your account is being used fraudulently</li>
                  <li>Your traffic patterns pose a risk to our network or other customers</li>
                  <li>You fail to pay any outstanding amounts</li>
                  <li>We are required to do so by law or regulation</li>
                </ul>
                <p className="mt-3">
                  You may close your account at any time by contacting support. Any remaining prepaid balance at the time of voluntary account closure may be reviewed for a partial refund at our discretion, less any applicable fees.
                </p>
              </Section>

              <Section number="12" title="Modifications to Terms">
                <p>
                  We reserve the right to modify these Terms at any time. Material changes will be communicated via email or a notice on the portal. Your continued use of VoipHub services after such changes constitutes your acceptance of the updated Terms. We recommend reviewing this page periodically.
                </p>
              </Section>

              <Section number="13" title="Governing Law">
                <p>
                  These Terms are governed by and construed in accordance with the laws of India. Any disputes arising from or relating to these Terms shall be subject to the exclusive jurisdiction of the courts located in India.
                </p>
              </Section>

              <Section number="14" title="Contact Information">
                <p>For questions, disputes, or notices regarding these Terms:</p>
                <div className="bg-slate-50 rounded-xl p-4 mt-3 space-y-2">
                  <p><strong>Email:</strong> <a href="mailto:support@voiphub.in" className="text-brand-500 hover:underline">support@voiphub.in</a></p>
                  <p><strong>Telegram:</strong> <a href="https://t.me/voiphub_support" target="_blank" rel="noopener noreferrer" className="text-brand-500 hover:underline">@voiphub_support</a></p>
                  <p><strong>Website:</strong> <a href="https://voiphub.in" className="text-brand-500 hover:underline">voiphub.in</a></p>
                </div>
              </Section>

              <div className="border-t border-slate-200 pt-6 mt-8">
                <p className="text-slate-500 text-sm leading-relaxed">
                  By registering an account or using VoipHub services, you confirm that you are at least 18 years of age,
                  have the legal authority to enter this agreement, and have read, understood, and agree to be bound by these Terms of Service.
                </p>
              </div>
            </div>

            <div className="flex justify-center gap-6 mt-8 text-sm">
              <Link to="/privacy" className="text-brand-500 hover:text-brand-600 font-medium">
                Privacy Policy →
              </Link>
              <Link to="/faq" className="text-brand-500 hover:text-brand-600 font-medium">
                FAQ →
              </Link>
              <Link to="/contact" className="text-brand-500 hover:text-brand-600 font-medium">
                Contact Us →
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TermsPage;
